import React from "react";
import { Modal, Button, Form, Col, Row, Spinner } from "react-bootstrap";
import { get } from "lodash";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { fetchData } from "../../helpers";
import { RESERVATION_COLOR } from "../../constants";
import moment from "moment";

const MySwal = withReactContent(Swal);

export default class BlockoutSlotsModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDate: moment().format("YYYY-MM-DD"),
      availableSlots: [],
      selectedSlots: [],
      selectedTableIds: [],
      selectedExperienceIds: [],
      blockout_reason: "",
      isSubmitting: false,
      isLoadingSlots: false,
      monthlyReservations: [],
      activeTab: "block", // "block" | "unblock"
      blockedReservations: [],
      selectedBlockoutIds: [],
      isLoadingBlocked: false,
      isUnblocking: false,
    };
    this.calendarRef = React.createRef();
  }

  componentDidMount() {
    this.fetchAvailableSlots(this.state.selectedDate);
    this.fetchBlockedReservations(this.state.selectedDate);
  }

  fetchAvailableSlots = async (date) => {
    setTimeout(() => {
      this.calendarRef.current?.getApi().updateSize();
    }, 100);
    this.setState({
      isLoadingSlots: true,
      availableSlots: [],
      selectedSlots: [],
    });
    try {
      const { store } = this.props;
      const { data } = await fetchData("post", `/reservation/tables`, {
        store_id: store._id,
        reservation_date: date,
        party_size: 2, // Using a default party size to get a representative list of slots.
      });

      const reservableTables =
        (data && data.result && data.result[0] && data.result[0].tables) || [];

      const slots = [
        ...new Set(reservableTables.flatMap((table) => table.time_slots)),
      ].sort((a, b) => a.localeCompare(b));

      this.setState({ availableSlots: slots, isLoadingSlots: false });
    } catch (error) {
      console.error("Failed to fetch slots", error);
      this.setState({ isLoadingSlots: false });
    }
  };

  fetchBlockedReservations = async (date) => {
    this.setState({ isLoadingBlocked: true });
    try {
      const { store } = this.props;
      const { data } = await fetchData(
        "get",
        `/reservation/${store._id}/block-slots?date=${date}`,
      );
      this.setState({
        blockedReservations: data?.result || [],
        isLoadingBlocked: false,
        selectedBlockoutIds: [],
      });
    } catch (error) {
      console.error("Failed to fetch blocked slots", error);
      this.setState({ blockedReservations: [], isLoadingBlocked: false });
    }
  };

  handleDateClick = (info) => {
    const dateStr = info.dateStr;
    this.setState({ selectedDate: dateStr, selectedBlockoutIds: [] });
    this.fetchAvailableSlots(dateStr);
    this.fetchBlockedReservations(dateStr);
  };

  handleEventClick = (info) => {
    const isBlockout = info.event.extendedProps?.isBlockout;
    const reservation = info.event.extendedProps?.reservation;
    if (isBlockout && reservation) {
      const timeStr = moment(reservation.order_time, "HH:mm").format("h:mm A");
      const dateStr = moment(reservation.order_date).format("MMM D, YYYY");
      MySwal.fire({
        title: "Release Blocked Slot?",
        html: `Are you sure you want to release the blockout at <b>${timeStr}</b> on <b>${dateStr}</b>?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Release Slot",
        confirmButtonColor: "#f64e60",
        cancelButtonText: "Cancel",
      }).then((result) => {
        if (result.isConfirmed) {
          this.handleUnblockSlots([reservation._id]);
        }
      });
    }
  };

  fetchCalendarReservations = async (
    fetchInfo,
    successCallback,
    failureCallback,
  ) => {
    const { store } = this.props;
    const start = moment(fetchInfo.start).format("YYYY-MM-DD");
    const end = moment(fetchInfo.end).format("YYYY-MM-DD");

    try {
      const { data } = await fetchData(
        "get",
        `/reservation/store/${store._id}/date-range?startDate=${start}&endDate=${end}`,
      );

      const allReservations = Object.values(data)
        .flat()
        .filter(
          (res) => !["cancelled", "cancelled_no_show"].includes(res.status),
        );

      this.setState({ monthlyReservations: allReservations });

      const events = allReservations.map((res) => {
        const isBlockout = res.reservation_notes?.source === "admin_blockout";
        const partySize =
          get(res, "order_details", []).find(
            (od) => od.item?.type === "reservation_experience",
          )?.qty || 0;
        return {
          id: res._id,
          title: isBlockout
            ? `🔒 ${res.reservation_notes?.reason || "Blocked"}`
            : `${res.guest_customer?.first_name || "Guest"} (${partySize}pax)`,
          start: `${res.order_date}T${res.order_time}`,
          allDay: false,
          backgroundColor: isBlockout ? "#f64e60" : "#FFA800",
          borderColor: isBlockout ? "#f64e60" : "#FFA800",
          textColor: "#fff",
          extendedProps: { isBlockout, reservation: res },
        };
      });

      successCallback(events);
    } catch (error) {
      console.error("Failed to fetch calendar reservations:", error);
      if (failureCallback) failureCallback(error);
    }
  };

  handleSelectAll = (type) => {
    const { tables, experiences } = this.props;
    if (type === "tables") {
      if (this.state.selectedTableIds.length === tables.length) {
        this.setState({ selectedTableIds: [] });
      } else {
        this.setState({ selectedTableIds: tables.map((t) => t._id) });
      }
    } else if (type === "experiences") {
      if (this.state.selectedExperienceIds.length === experiences.length) {
        this.setState({ selectedExperienceIds: [] });
      } else {
        this.setState({ selectedExperienceIds: experiences.map((e) => e._id) });
      }
    } else if (type === "slots") {
      if (
        this.state.selectedSlots.length === this.state.availableSlots.length
      ) {
        this.setState({ selectedSlots: [] });
      } else {
        this.setState({ selectedSlots: [...this.state.availableSlots] });
      }
    }
  };

  handleItemSelect = (id, type) => {
    const key =
      type === "tables" ? "selectedTableIds" : "selectedExperienceIds";
    const currentIds = this.state[key];
    if (currentIds.includes(id)) {
      this.setState({
        [key]: currentIds.filter((existingId) => existingId !== id),
      });
    } else {
      this.setState({ [key]: [...currentIds, id] });
    }
  };

  handleSlotSelect = (slot) => {
    const { selectedSlots } = this.state;
    if (selectedSlots.includes(slot)) {
      this.setState({ selectedSlots: selectedSlots.filter((s) => s !== slot) });
    } else {
      this.setState({ selectedSlots: [...selectedSlots, slot] });
    }
  };

  handleBlockoutSelect = (id) => {
    const { selectedBlockoutIds } = this.state;
    if (selectedBlockoutIds.includes(id)) {
      this.setState({
        selectedBlockoutIds: selectedBlockoutIds.filter((item) => item !== id),
      });
    } else {
      this.setState({
        selectedBlockoutIds: [...selectedBlockoutIds, id],
      });
    }
  };

  handleSelectAllBlockouts = () => {
    const { selectedBlockoutIds, blockedReservations } = this.state;
    if (selectedBlockoutIds.length === blockedReservations.length) {
      this.setState({ selectedBlockoutIds: [] });
    } else {
      this.setState({
        selectedBlockoutIds: blockedReservations.map((b) => b._id),
      });
    }
  };

  handleUnblockSlots = async (orderIds) => {
    if (!orderIds || !orderIds.length) return;
    this.setState({ isUnblocking: true });
    try {
      const { store } = this.props;
      const { data } = await fetchData(
        "post",
        `/reservation/${store._id}/unblock-slots`,
        { orderIds },
      );

      MySwal.fire({
        icon: "success",
        title: "Released!",
        text: data?.message || "Blocked slots have been released.",
        timer: 2000,
        showConfirmButton: false,
      });

      await this.fetchAvailableSlots(this.state.selectedDate);
      await this.fetchBlockedReservations(this.state.selectedDate);
      this.calendarRef.current?.getApi().refetchEvents();
      if (this.props.onSuccess) this.props.onSuccess();
    } catch (err) {
      console.error("Failed to unblock slots", err);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          "Failed to release blocked slots. Please try again.",
      });
    } finally {
      this.setState({ isUnblocking: false });
    }
  };

  handleSubmit = async () => {
    const {
      selectedDate,
      selectedSlots,
      selectedTableIds,
      selectedExperienceIds,
      blockout_reason,
    } = this.state;
    const { store, onHide, onSuccess, tables } = this.props;

    if (
      selectedSlots.length === 0 ||
      selectedTableIds.length === 0 ||
      selectedExperienceIds.length === 0
    ) {
      return MySwal.fire({
        icon: "warning",
        title: "Incomplete Selection",
        text: "Please select a date, at least one slot, one table, and one experience.",
      });
    }

    this.setState({ isSubmitting: true });

    try {
      // Check for conflicting reservations
      const { data: reservationData } = await fetchData(
        "get",
        `/reservation/store/${store._id}?date=${selectedDate}`,
      );
      const activeReservations = [
        ...(reservationData.upcoming || []),
        ...(reservationData.seated || []),
      ];

      if (activeReservations.length > 0) {
        const tableIdToItemIdMap = new Map(
          tables.map((t) => [t._id, t.item?._id]).filter((entry) => entry[1]),
        );
        const selectedItemIds = selectedTableIds.map((id) =>
          tableIdToItemIdMap.get(id),
        );

        const conflicts = activeReservations.filter((res) => {
          const isSlotConflict = selectedSlots.includes(res.order_time);
          if (!isSlotConflict) return false;

          const tableDetail = res.order_details.find(
            (od) => od.item.type === "reservation",
          );
          if (!tableDetail) return false;

          const tableItemId = tableDetail.item._id;
          return selectedItemIds.includes(tableItemId);
        });

        if (conflicts.length > 0) {
          this.setState({ isSubmitting: false });
          return MySwal.fire({
            icon: "warning",
            title: "Existing Reservations Found",
            html: `Some of the selected slots are already booked. Please cancel or reschedule the existing reservations manually before blocking out these slots.`,
          });
        }
      }

      const payload = {
        reservation_date: selectedDate,
        slots: selectedSlots,
        tableIds: selectedTableIds,
        experienceIds: selectedExperienceIds,
        blockout_reason: blockout_reason,
      };

      await fetchData("post", `/reservation/${store._id}/block-slots`, payload);

      MySwal.fire({
        icon: "success",
        title: "Success!",
        text: "Selected slots have been blocked out.",
        timer: 2000,
        showConfirmButton: false,
      });

      await this.fetchAvailableSlots(selectedDate);
      await this.fetchBlockedReservations(selectedDate);
      this.calendarRef.current?.getApi().refetchEvents();

      if (onSuccess) onSuccess();
      onHide();
    } catch (err) {
      console.error(err);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          "Failed to block out slots. Please try again.",
      });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  getTitleFormat = (dateArg) => {
    const { selectedDate } = this.state;
    const year = dateArg?.start?.year ?? dateArg?.date?.year;
    const month = dateArg?.start?.month ?? dateArg?.date?.month;

    if (selectedDate && year !== undefined && month !== undefined) {
      const selected = moment(selectedDate);
      if (year === selected.year() && month === selected.month()) {
        return selected.format("MMMM D, YYYY");
      }
      return moment([year, month, 1]).format("MMMM YYYY");
    }

    return selectedDate
      ? moment(selectedDate).format("MMMM D, YYYY")
      : moment().format("MMMM D, YYYY");
  };

  dayCellContent = (arg) => {
    const isSelected =
      moment(arg.date).format("YYYY-MM-DD") === this.state.selectedDate;
    const dayReservations = this.state.monthlyReservations.filter((r) =>
      moment(r.order_date).isSame(arg.date, "day"),
    );
    const blockoutCount = dayReservations.filter(
      (r) => r.reservation_notes?.source === "admin_blockout",
    ).length;
    const regularReservations = dayReservations.filter(
      (r) => r.reservation_notes?.source !== "admin_blockout",
    );
    const totalPax = regularReservations.reduce((acc, res) => {
      const guestCount = res.order_details
        ? res.order_details
            .filter((detail) => detail.item?.type === "reservation_experience")
            .reduce((subAcc, cur) => subAcc + (cur.qty || 0), 0)
        : res.guest_count || 0;
      return acc + guestCount;
    }, 0);

    return (
      <>
        <div className="d-flex align-items-center justify-content-between">
          <span className={isSelected ? "font-weight-bolder" : ""}>
            {arg.dayNumberText}
          </span>
          {isSelected && (
            <span
              className="badge badge-light-primary px-1 py-0 font-weight-boldest"
              style={{
                fontSize: "0.6rem",
                color: RESERVATION_COLOR,
                lineHeight: "1.1",
              }}
            >
              SELECTED
            </span>
          )}
        </div>
        {regularReservations.length > 0 && (
          <div
            style={{
              fontSize: "0.7em",
              color: "#FFA800",
              fontWeight: "bold",
              marginTop: "2px",
            }}
          >
            {regularReservations.length} res ({totalPax}pax)
          </div>
        )}
        {blockoutCount > 0 && (
          <div
            style={{
              fontSize: "0.7em",
              color: "#f64e60",
              fontWeight: "bold",
              marginTop: "1px",
            }}
          >
            🔒 {blockoutCount} blocked
          </div>
        )}
      </>
    );
  };

  renderBlockedReservationsTab = () => {
    const {
      blockedReservations,
      isLoadingBlocked,
      selectedBlockoutIds,
      isUnblocking,
      selectedDate,
    } = this.state;

    if (isLoadingBlocked) {
      return (
        <div className="text-center py-10">
          <Spinner animation="border" variant="danger" />
          <p className="text-muted mt-2">Loading blocked slots...</p>
        </div>
      );
    }

    if (!blockedReservations || blockedReservations.length === 0) {
      return (
        <div className="text-center py-10 bg-light rounded p-6">
          <i className="fas fa-calendar-check text-muted fa-3x mb-3"></i>
          <h6 className="font-weight-bold">No Blocked Slots</h6>
          <p className="text-muted mb-4">
            There are no blocked reservations on{" "}
            {moment(selectedDate).format("MMM D, YYYY")}.
          </p>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => this.setState({ activeTab: "block" })}
          >
            <i className="fas fa-plus mr-1"></i> Block a Slot on this Date
          </Button>
        </div>
      );
    }

    const allSelected =
      selectedBlockoutIds.length === blockedReservations.length &&
      blockedReservations.length > 0;

    return (
      <div>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              className="p-0 text-danger font-weight-bold"
              size="sm"
              onClick={this.handleSelectAllBlockouts}
            >
              {allSelected ? "Deselect All" : "Select All"}
            </Button>
            <span className="text-muted ml-3 font-size-sm">
              {blockedReservations.length} slot(s) blocked
            </span>
          </div>

          <Button
            variant="danger"
            size="sm"
            disabled={isUnblocking}
            onClick={() => {
              const ids =
                selectedBlockoutIds.length > 0
                  ? selectedBlockoutIds
                  : blockedReservations.map((b) => b._id);
              MySwal.fire({
                title: `Release ${ids.length} Blocked Slot(s)?`,
                text: "These slots will immediately become available for customer bookings again.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, Release",
                confirmButtonColor: "#f64e60",
                cancelButtonText: "Cancel",
              }).then((result) => {
                if (result.isConfirmed) {
                  this.handleUnblockSlots(ids);
                }
              });
            }}
          >
            {isUnblocking ? (
              <Spinner as="span" animation="border" size="sm" />
            ) : selectedBlockoutIds.length > 0 ? (
              `Release Selected (${selectedBlockoutIds.length})`
            ) : (
              `Release All for Date (${blockedReservations.length})`
            )}
          </Button>
        </div>

        <div style={{ maxHeight: "400px", overflowY: "auto" }} className="pr-1">
          {blockedReservations.map((res) => {
            const isSelected = selectedBlockoutIds.includes(res._id);
            const timeStr = moment(res.order_time, "HH:mm").format("h:mm A");
            const tableName =
              res.reservation_notes?.table?.name ||
              res.order_details?.find((od) => od.item?.type === "reservation")
                ?.item?.name ||
              "All Tables";
            const expName =
              res.order_details?.find(
                (od) => od.item?.type === "reservation_experience",
              )?.item?.name || "";
            const reason =
              res.reservation_notes?.reason || "Blocked out by admin";

            return (
              <div
                key={res._id}
                className={`p-3 rounded mb-2 border d-flex align-items-center justify-content-between ${
                  isSelected
                    ? "bg-light-danger border-danger shadow-sm"
                    : "bg-white"
                }`}
                style={{ cursor: "pointer", transition: "all 0.15s ease" }}
                onClick={() => this.handleBlockoutSelect(res._id)}
              >
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    className="mr-3"
                    checked={isSelected}
                    onChange={() => this.handleBlockoutSelect(res._id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div>
                    <div className="d-flex align-items-center">
                      <span className="font-weight-bolder text-dark mr-2 font-size-base">
                        {timeStr}
                      </span>
                      <span className="badge badge-light-danger font-weight-bold">
                        <i className="fas fa-chair mr-1 text-danger"></i>
                        {tableName}
                      </span>
                      {expName && (
                        <span className="badge badge-light font-weight-bold ml-1 text-muted">
                          {expName}
                        </span>
                      )}
                    </div>
                    <div className="text-muted font-size-xs mt-1">
                      <i className="fas fa-info-circle mr-1"></i>
                      {reason}
                    </div>
                  </div>
                </div>

                <Button
                  variant="outline-danger"
                  size="sm"
                  className="btn-icon ml-2"
                  title="Release this blocked slot"
                  disabled={isUnblocking}
                  onClick={(e) => {
                    e.stopPropagation();
                    MySwal.fire({
                      title: "Release This Slot?",
                      html: `Release blockout at <b>${timeStr}</b> (${tableName})?`,
                      icon: "question",
                      showCancelButton: true,
                      confirmButtonText: "Yes, Release",
                      confirmButtonColor: "#f64e60",
                      cancelButtonText: "Cancel",
                    }).then((result) => {
                      if (result.isConfirmed) {
                        this.handleUnblockSlots([res._id]);
                      }
                    });
                  }}
                >
                  <i className="fas fa-trash-alt"></i>
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  render() {
    const { show, onHide, tables = [], experiences = [] } = this.props;
    const {
      selectedDate,
      availableSlots,
      selectedSlots,
      selectedTableIds,
      selectedExperienceIds,
      blockout_reason,
      isSubmitting,
      isLoadingSlots,
      activeTab,
      blockedReservations,
      selectedBlockoutIds,
      isUnblocking,
    } = this.state;

    return (
      <Modal show={show} onHide={onHide} size="xl" centered>
        <style>
          {`
            .modal-content .btn-primary, .bg-primary {
              background-color: ${RESERVATION_COLOR} !important;
              border-color: ${RESERVATION_COLOR} !important;
            }
            .modal-content .text-primary {
              color: ${RESERVATION_COLOR} !important;
            }
            .fc-daygrid-more-link {
              color: ${RESERVATION_COLOR};
            }
            .fc-toolbar-title {
              font-size: 1.15rem !important;
              font-weight: 700 !important;
              color: #181C32 !important;
            }
            .blockout-selected-date {
              background-color: ${RESERVATION_COLOR}18 !important;
              box-shadow: inset 0 0 0 2px ${RESERVATION_COLOR} !important;
              border-radius: 4px;
            }
            .blockout-selected-date .fc-daygrid-day-number {
              color: ${RESERVATION_COLOR} !important;
              font-weight: 800 !important;
            }
          `}
        </style>
        <Modal.Header closeButton>
          <Modal.Title>Blockout Reservation Slots</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={5}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <h6 className="font-weight-bold mb-0">1. Select Date</h6>
              </div>
              <div
                className="p-3 mb-3 rounded border d-flex align-items-center justify-content-between shadow-xs"
                style={{
                  backgroundColor: "#F3F6F9",
                  borderLeft: `4px solid ${RESERVATION_COLOR}`,
                }}
              >
                <div>
                  <div className="text-uppercase text-muted font-weight-bold font-size-xs">
                    Selected Date
                  </div>
                  <div className="font-weight-bolder text-dark font-size-base mt-1">
                    <i
                      className="far fa-calendar-check mr-2"
                      style={{ color: RESERVATION_COLOR }}
                    ></i>
                    {moment(selectedDate).format("dddd, MMMM D, YYYY")}
                  </div>
                </div>
                <span
                  className="badge text-white font-weight-boldest px-3 py-2"
                  style={{ backgroundColor: RESERVATION_COLOR }}
                >
                  {moment(selectedDate).format("MMM D")}
                </span>
              </div>
              <FullCalendar
                ref={this.calendarRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                dateClick={this.handleDateClick}
                eventClick={this.handleEventClick}
                validRange={{ start: new Date() }}
                titleFormat={this.getTitleFormat}
                dayCellClassNames={(arg) =>
                  arg.dateStr === selectedDate ? ["blockout-selected-date"] : []
                }
                events={this.fetchCalendarReservations}
                dayCellContent={this.dayCellContent}
                dayMaxEvents={2}
                eventDisplay="block"
              />
            </Col>
            <Col md={7}>
              <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom">
                <div className="btn-group">
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      activeTab === "block"
                        ? "btn-primary"
                        : "btn-light-primary text-primary"
                    }`}
                    onClick={() => this.setState({ activeTab: "block" })}
                  >
                    <i className="fas fa-ban mr-1"></i> Block Slots
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      activeTab === "unblock"
                        ? "btn-danger"
                        : "btn-light-danger text-danger"
                    }`}
                    onClick={() => this.setState({ activeTab: "unblock" })}
                  >
                    <i className="fas fa-unlock mr-1"></i> Active Blockouts
                    {blockedReservations.length > 0 && (
                      <span className="badge badge-light-danger ml-2 font-weight-bolder">
                        {blockedReservations.length}
                      </span>
                    )}
                  </button>
                </div>
                <span className="badge badge-light-primary text-primary font-size-sm font-weight-bolder px-3 py-2">
                  <i className="far fa-calendar-alt mr-1 text-primary"></i>
                  {moment(selectedDate).format("MMMM D, YYYY")}
                </span>
              </div>

              {activeTab === "unblock" ? (
                this.renderBlockedReservationsTab()
              ) : (
                <Form>
                  <Form.Group>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <Form.Label className="font-weight-bold mb-0">
                        Available Slots for{" "}
                        {moment(selectedDate).format("MMM D, YYYY")}
                      </Form.Label>
                      {availableSlots.length > 0 && (
                        <Button
                          variant="link"
                          className="p-0 text-primary"
                          size="sm"
                          onClick={() => this.handleSelectAll("slots")}
                        >
                          {selectedSlots.length === availableSlots.length
                            ? "Deselect All"
                            : "Select All"}
                        </Button>
                      )}
                    </div>
                    {isLoadingSlots ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <div className="d-flex flex-wrap">
                        {availableSlots.length > 0 ? (
                          availableSlots.map((slot) => {
                            const isSelected = selectedSlots.includes(slot);
                            return (
                              <div
                                key={slot}
                                style={{
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                }}
                                className={`px-4 py-2 rounded-lg border text-center mr-2 mb-2 ${
                                  isSelected
                                    ? "bg-primary border-primary shadow-sm text-white"
                                    : "bg-light border-transparent bg-hover-light text-dark-75"
                                }`}
                                onClick={() => this.handleSlotSelect(slot)}
                              >
                                <span
                                  className="font-weight-boldest"
                                  style={{ fontSize: "0.9rem" }}
                                >
                                  {moment(slot, "HH:mm").format("h:mm A")}
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-muted w-100">
                            No slots available for this date.
                          </p>
                        )}
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group>
                    <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
                      <Form.Label className="font-weight-bold mb-0">
                        Tables
                      </Form.Label>
                      {tables.length > 0 && (
                        <Button
                          variant="link"
                          className="p-0 text-primary"
                          size="sm"
                          onClick={() => this.handleSelectAll("tables")}
                        >
                          {selectedTableIds.length === tables.length
                            ? "Deselect All"
                            : "Select All"}
                        </Button>
                      )}
                    </div>
                    <div className="d-flex flex-wrap">
                      {tables.map((table) => {
                        const isSelected = selectedTableIds.includes(table._id);
                        return (
                          <div
                            key={table._id}
                            style={{
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            className={`px-4 py-2 rounded-lg border d-flex align-items-center mr-2 mb-2 ${
                              isSelected
                                ? "bg-primary border-primary shadow-sm text-white"
                                : "bg-light border-transparent bg-hover-light text-dark-75"
                            }`}
                            onClick={() =>
                              this.handleItemSelect(table._id, "tables")
                            }
                          >
                            <i
                              className={`fas fa-chair mr-2 text-primary ${
                                isSelected ? "text-white" : ""
                              }`}
                            ></i>
                            <span
                              className="font-weight-boldest"
                              style={{ fontSize: "0.9rem" }}
                            >
                              {table.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Form.Group>

                  <Form.Group>
                    <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
                      <Form.Label className="font-weight-bold mb-0">
                        Experiences
                      </Form.Label>
                      {experiences.length > 0 && (
                        <Button
                          variant="link"
                          className="p-0 text-primary"
                          size="sm"
                          onClick={() => this.handleSelectAll("experiences")}
                        >
                          {selectedExperienceIds.length === experiences.length
                            ? "Deselect All"
                            : "Select All"}
                        </Button>
                      )}
                    </div>
                    <div className="d-flex flex-wrap">
                      {experiences.map((exp) => {
                        const isSelected = selectedExperienceIds.includes(
                          exp._id,
                        );
                        return (
                          <div
                            key={exp._id}
                            style={{
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            className={`px-4 py-2 rounded-lg border d-flex align-items-center mr-2 mb-2 ${
                              isSelected
                                ? "bg-primary border-primary shadow-sm text-white"
                                : "bg-light border-transparent bg-hover-light text-dark-75"
                            }`}
                            onClick={() =>
                              this.handleItemSelect(exp._id, "experiences")
                            }
                          >
                            <i
                              className={`fas fa-utensils mr-2 text-primary ${
                                isSelected ? "text-white" : ""
                              }`}
                            ></i>
                            <span
                              className="font-weight-boldest"
                              style={{ fontSize: "0.9rem" }}
                            >
                              {exp.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className="font-weight-bold mt-3">
                      Reason (Optional)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="e.g., Private event, Maintenance"
                      value={blockout_reason}
                      onChange={(e) =>
                        this.setState({ blockout_reason: e.target.value })
                      }
                    />
                  </Form.Group>
                </Form>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={isSubmitting || isUnblocking}
          >
            Close
          </Button>
          {activeTab === "block" ? (
            <Button
              variant="primary"
              onClick={this.handleSubmit}
              disabled={
                isSubmitting ||
                isLoadingSlots ||
                selectedSlots.length === 0 ||
                selectedTableIds.length === 0 ||
                selectedExperienceIds.length === 0
              }
              style={{
                backgroundColor: RESERVATION_COLOR,
                borderColor: RESERVATION_COLOR,
              }}
            >
              {isSubmitting ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Blockout Slots"
              )}
            </Button>
          ) : (
            blockedReservations.length > 0 && (
              <Button
                variant="danger"
                onClick={() => {
                  const ids =
                    selectedBlockoutIds.length > 0
                      ? selectedBlockoutIds
                      : blockedReservations.map((b) => b._id);
                  MySwal.fire({
                    title: `Release ${ids.length} Blocked Slot(s)?`,
                    text: "These slots will immediately become available for customer bookings again.",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, Release",
                    confirmButtonColor: "#f64e60",
                    cancelButtonText: "Cancel",
                  }).then((result) => {
                    if (result.isConfirmed) {
                      this.handleUnblockSlots(ids);
                    }
                  });
                }}
                disabled={isUnblocking}
              >
                {isUnblocking ? (
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : selectedBlockoutIds.length > 0 ? (
                  `Release Selected (${selectedBlockoutIds.length})`
                ) : (
                  `Release All on Date (${blockedReservations.length})`
                )}
              </Button>
            )
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}
